import React from 'react';
import dayjs from 'dayjs';
import { action, observable, makeObservable, computed } from 'mobx';
import { apiAuthCaller } from '@services/apiCaller';
import { IRootStore } from '@stores/index';
import { ISource } from '@stores/source';
import { IDestinationStore } from '@stores/destination';
import { ITransformationStore } from '@stores/transformation';
import { ResourceCategory, ResourceType } from '@stores/alertDefinitionsList/types';
import sourceFactory from '@components/sources/sourceFactory';
import { readableThreshold } from '../utils';
import { Path } from '@components/routes/constants';

const ALERTS_API = '/alertNotifications/alerts/active';

interface RawActiveAlert {
  sourceId?: string;
  destinationId?: string;
  trackingPlanId?: string;
  jobId?: string;
  transformationId?: string;
  alertDefinitionName: string;
  activeAt: string;
  threshold?: number;
  value?: number;
  currentHourEventCount?: number;
  pastWeekHourEventCount?: number;
}

export interface ActiveAlert extends Omit<RawActiveAlert, 'activeAt' | 'threshold' | 'value'> {
  resourceCategory: ResourceCategory;
  resourceType: ResourceType;
  alertDisplayName: string;
  alertMessage: string;
  activeAt: dayjs.Dayjs;
  threshold: string;
  value?: string;
  sourceName?: string;
  destinationName?: string;
  trackingPlanName?: string;
  transformationName?: string;
}

type AlertConfigurationStatus =
  | {
      hasEventStreamDestinationAlerts: boolean;
      hasRETLAlerts: boolean;
      hasTrackingPlanAlerts: boolean;
    }
  | {
      hasAlerts: boolean;
    };

interface RawActiveAlertsData {
  activeAlerts: RawActiveAlert[];
  alertConfigurationStatus: AlertConfigurationStatus;
}

export type ResourceDetails =
  | {
      resource: ISource;
      resourceType: ResourceType.SOURCE;
      resourceCategory: ResourceCategory.EVENT_STREAM | ResourceCategory.RETL;
      resourceDefDisplayName: string;
      resourcePath: string;
    }
  | {
      resource: IDestinationStore;
      resourceType: ResourceType.CLOUD_DESTINATION | ResourceType.WAREHOUSE_DESTINATION;
      resourceCategory: ResourceCategory.EVENT_STREAM | ResourceCategory.RETL;
      resourceDefDisplayName: string;
      resourcePath: string;
    }
  | {
      resource: ITransformationStore;
      resourceType: ResourceType.TRANSFORMATION;
      resourceCategory: ResourceCategory.EVENT_STREAM;
      resourceDefDisplayName: string;
      resourcePath: string;
    };

export interface IActiveAlertsStore {
  resourceDetails: ResourceDetails | undefined;
  activeAlerts: ActiveAlert[];
  alertConfigurationStatus: AlertConfigurationStatus | null;
  resourceAlertsConfigured: boolean;
  activeAlertsLoading: boolean;
  showActiveAlerts: boolean;
  load(): Promise<void>;
  getResourceDetails(resource: ResourceDetails['resource']): ResourceDetails;
}

export class ActiveAlertsStore implements IActiveAlertsStore {
  protected rootStore: IRootStore;

  private resource: ResourceDetails['resource'] | undefined;

  @observable public activeAlerts: ActiveAlert[] = [];

  @observable public alertConfigurationStatus: AlertConfigurationStatus | null = null;

  @observable public activeAlertsLoading = false;

  @observable private errorInLoadingActiveAlerts = false;

  constructor(rootStore: IRootStore, resource?: ResourceDetails['resource']) {
    makeObservable(this);
    this.rootStore = rootStore;
    this.resource = resource;
  }

  @computed get resourceDetails() {
    return this.resource ? this.getResourceDetails(this.resource) : undefined;
  }

  private isSource = (resource: ResourceDetails['resource']): resource is ISource =>
    'sourceDef' in resource;

  private isDestination = (resource: ResourceDetails['resource']): resource is IDestinationStore =>
    'destinationDefinition' in resource;

  public getResourceDetails = (resource: ResourceDetails['resource']): ResourceDetails => {
    if (this.isSource(resource)) {
      return {
        resource,
        resourceType: ResourceType.SOURCE,
        resourceCategory: sourceFactory(resource.sourceDef.category).isWarehouse()
          ? ResourceCategory.RETL
          : ResourceCategory.EVENT_STREAM,
        resourceDefDisplayName: resource.sourceDef.displayName,
        resourcePath: Path.SOURCE_DETAILS(resource.id),
      };
    }

    if (this.isDestination(resource)) {
      return {
        resource,
        resourceType:
          resource.destinationDefinition.category === 'warehouse'
            ? ResourceType.WAREHOUSE_DESTINATION
            : ResourceType.CLOUD_DESTINATION,
        resourceCategory:
          resource.sources.length > 0 &&
          resource.sources.every((source) => source.sourceCategory.category === 'retl')
            ? ResourceCategory.RETL
            : ResourceCategory.EVENT_STREAM,
        resourceDefDisplayName: resource.destinationDefinition.displayName,
        resourcePath: Path.DESTINATION_DETAILS(resource.id),
      };
    }

    return {
      resource,
      resourceType: ResourceType.TRANSFORMATION,
      resourceCategory: ResourceCategory.EVENT_STREAM,
      resourceDefDisplayName: 'Transformation',
      resourcePath: Path.TRANSFORMATION_DETAILS(resource.id),
    };
  };

  @computed
  private get canFetchActiveAlerts() {
    const isDestination =
      this.resourceDetails?.resourceType === ResourceType.CLOUD_DESTINATION ||
      this.resourceDetails?.resourceType === ResourceType.WAREHOUSE_DESTINATION;
    const isRetlDestination =
      isDestination && this.resourceDetails?.resourceCategory === ResourceCategory.RETL;

    return this.rootStore.featuresStore.has('NOTIFICATION_SUBSCRIPTIONS') && !isRetlDestination;
  }

  @computed
  public get showActiveAlerts() {
    return this.canFetchActiveAlerts && !this.errorInLoadingActiveAlerts;
  }

  @computed
  public get resourceAlertsConfigured() {
    if (
      this.alertConfigurationStatus &&
      typeof this.alertConfigurationStatus === 'object' &&
      'hasAlerts' in this.alertConfigurationStatus
    ) {
      return this.alertConfigurationStatus.hasAlerts;
    }
    return false;
  }

  @action.bound
  public async load() {
    this.errorInLoadingActiveAlerts = false;

    if (!this.canFetchActiveAlerts) {
      return;
    }
    const { messagesStore, alertDefinitionsListStore } = this.rootStore;
    this.activeAlertsLoading = true;
    const { resource, resourceType, resourceCategory } = this.resourceDetails || {};

    try {
      const resp = await Promise.all([
        apiAuthCaller().get<RawActiveAlertsData>(ALERTS_API, {
          params: {
            resourceId: resource?.id,
            resourceType,
            resourceCategory,
          },
        }),
        alertDefinitionsListStore.load(),
      ]);
      const { data } = resp[0];

      this.activeAlerts = this.constructActiveAlerts(data.activeAlerts);

      this.alertConfigurationStatus = data.alertConfigurationStatus;
    } catch (e) {
      messagesStore.showErrorMessage('Failed to load active alerts');
      this.errorInLoadingActiveAlerts = true;
    } finally {
      this.activeAlertsLoading = false;
    }
  }

  @action
  private constructActiveAlerts = (rawActiveAlerts: RawActiveAlert[]) => {
    const {
      alertDefinitionsListStore: { alertDefinitions },
      sourcesListStore: { sourceById },
      destinationsListStore: { destinationById },
      retlConnectionListStore: { getConnectionById },
      trackingPlanListStore,
      transformationsListStore,
    } = this.rootStore;

    const alerts = rawActiveAlerts.map((a) => {
      const alertDef = alertDefinitions.find((alertDef) => alertDef.name === a.alertDefinitionName);

      if (!alertDef) {
        return null;
      }

      const source = sourceById(a?.sourceId || '');
      const destination = destinationById(a.destinationId || '');
      const connection = getConnectionById(a.jobId || '');
      const connectionDestination = destinationById(connection?.destinationId || '');
      const trackingPlan = trackingPlanListStore.getById(a.trackingPlanId || '');
      const transformation = transformationsListStore.getById(a.transformationId || '');

      return {
        ...a,
        activeAt: dayjs(a.activeAt),
        alertMessage: alertDef.messageTemplate,
        alertDisplayName: alertDef.displayName,
        resourceCategory: alertDef.resourceCategory,
        resourceType: alertDef.resourceType,
        threshold: readableThreshold(a.threshold, alertDef?.thresholdMetadata?.unit),
        value: readableThreshold(a.value, alertDef?.thresholdMetadata?.unit),
        sourceName: source?.name || '',
        destinationName: destination?.name || connectionDestination?.name || '',
        trackingPlanName: trackingPlan?.name || '',
        transformationName: transformation?.name || '',
      };
    });

    return alerts.filter((alert) => alert !== null) as ActiveAlert[];
  };
}

export const ActiveAlertsStoreCtx = React.createContext<IActiveAlertsStore | undefined>(undefined);

export const useActiveAlertsStore = () =>
  React.useContext(ActiveAlertsStoreCtx) as IActiveAlertsStore;
